/**
 * The line at the top of the pantry that says what needs eating.
 *
 * This is what the "expiry banner" setting controls. It is deliberately not a
 * push notification — the app has no server and asks for no permissions — so
 * the setting is honest about being an in-app banner.
 */

import { daysUntilExpiry } from "../features/food/expiry";
import type { FoodItem } from "../features/food/types";
import { useSettings } from "../features/settings/SettingsContext";
import { Icon } from "./Icon";

import "./ExpiryBand.css";

/** Anything inside this window counts as needing attention. */
const SOON_DAYS = 3;

interface ExpiryBandProps {
  items: FoodItem[];
  today: string;
}

export function ExpiryBand({ items, today }: ExpiryBandProps) {
  const { settings } = useSettings();
  if (settings.notificationFrequency === "none") return null;

  const pressing = items
    .map((item) => ({ item, days: daysUntilExpiry(item, today) }))
    .filter(({ days }) => days <= SOON_DAYS)
    .sort((a, b) => a.days - b.days);

  // "Only when something's going off" means exactly that.
  if (pressing.length === 0 && settings.notificationFrequency === "expiring-only") return null;

  if (pressing.length === 0) {
    return (
      <p className="band" data-tone="calm">
        <Icon name="check" className="band__icon" />
        Nothing needs eating today.
      </p>
    );
  }

  const [first] = pressing;
  const others = pressing.length - 1;

  return (
    <p className="band" data-tone="pressing">
      <Icon name="clock" className="band__icon" />
      <span>
        <strong className="band__name">{first.item.name}</strong>{" "}
        {first.days < 0
          ? "is past its date"
          : first.days === 0
            ? "needs eating today"
            : first.days === 1
              ? "needs eating tomorrow"
              : `has ${first.days} days left`}
        {others > 0 && `, and ${others} other${others === 1 ? "" : "s"} soon`}.
      </span>
    </p>
  );
}
